import React, { useState, useEffect } from "react";
import { BiChevronRight } from "react-icons/bi";
import {
  ButtonComp,
  CardComp,
  ModalCompAdvanceUpdate,
  MoneyInputComp,
  SelectComp2,
  TextInputComp,
} from "../../../components";
import { Formik, Form } from "formik";
import FilterFind from "../../../components/FilterFind";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useEnums } from "../../../hooks/useEnums";
import NonmemberGuarantorForm from "../../Admin/LoanManagement/modules/NonmemberGuarantorForm";
import { useNavigate } from "react-router-dom";
import {
  useCreateMemberLoanRequestMutation,
  useGetMemberLoanTypesQuery,
  useGetMemberBorrowingEligibilityQuery,
} from "../../../store/member/member-loan";
import { useGetAllMembersAsMemberUserQuery } from "../../../store/selectableSlice";
import { useUser } from "../../../hooks";
import LoanTypeDetails from "../../../components/Common/LoanTypeDetails";

export default function LoanApplication() {
  const enums = useEnums();
  const user = useUser();
  const navigate = useNavigate();
  const [loanTypeDetails, setLoanTYpeDetails] = useState({});
  const [guarantors, setGuarantors] = useState([]);
  const [nonMembersGuarantorDetails, setNonMemberGuarantorDetails] = useState(
    []
  );
  const [modalPage, setModalPage] = React.useState("");

  const { data: loanTypes, isLoading: isLoadingLoanTypes } =
    useGetMemberLoanTypesQuery();

  const formattedLoanTypes = loanTypes?.data?.configs?.map((item) => {
    return {
      label: item.name,
      value: item.id,
      id: item.id,
      max_guarantor: item.max_guarantor,
      min_guarantor: item.min_guarantor,
      min_loan_amount: item.min_loan_amount,
      max_loan_amount: item.max_loan_amount,
      rate: item.rate,
      interest_calculation_mode: item.interest_calculation_mode,
      loan_fee: item.loan_fee,
      // Include all other loan type fields for the details component
      ...item,
    };
  });

  const { data: borrowingEligibility } = useGetMemberBorrowingEligibilityQuery(
    { loanTypeId: loanTypeDetails?.id || loanTypeDetails?.value },
    { skip: !(loanTypeDetails?.id || loanTypeDetails?.value) }
  );

  useEffect(() => {
    const maxG = Number(loanTypeDetails?.max_guarantor ?? 0);
    if (maxG > 0) {
      const initialGuarantors = Array.from({ length: maxG }, (_, index) => ({
        label: `Guarantor ${index + 1}`,
        value: "",
      }));
      setGuarantors(initialGuarantors);
    } else {
      setGuarantors([]);
      setNonMemberGuarantorDetails([]);
    }
  }, [loanTypeDetails?.max_guarantor]);

  const { data: membersList } = useGetAllMembersAsMemberUserQuery();

  const { allMembers } = React.useMemo(() => {
    const fetchedMembers = Array.isArray(membersList) ? membersList : [];
    const selfId = user?.member_id || user?.id;
    const formattedMembers = fetchedMembers
      .filter((item) => item && (item.member_id || item.id))
      .map((item) => {
        const first = item.first_name || "";
        const last = item.last_name || "";
        const name = `${first} ${last}`.trim() || item.name || "Unknown";
        const membershipNo = item.member_no || "N/A";
        const value = item.member_id || item.id;
        const canGuarantee = item.can_guarantee !== false;
        const reason = item.guarantee_block_reason || null;
        return {
          label: reason
            ? `${name} | Membership No: ${membershipNo} (${reason})`
            : `${name} | Membership No: ${membershipNo}`,
          name,
          photo: item.photo,
          userId: item.member_no,
          // API still expects internal id for guarantor payload
          value,
          isDisabled: !canGuarantee || (selfId && value === selfId),
          can_guarantee: canGuarantee,
          guarantee_block_reason: reason,
        };
      });
    return { allMembers: formattedMembers };
  }, [membersList, user?.member_id, user?.id]);

  const handleGuarantorChange = (index, value, setFieldValue) => {
    if (value && !String(value).includes("non_member_guarantor")) {
      const option = allMembers.find((m) => m.value === value);
      if (option?.isDisabled) {
        toast.error(option.guarantee_block_reason || "This member cannot guarantee");
        return;
      }
    }
    const updatedGuarantors = [...guarantors];
    updatedGuarantors[index].value = value;
    setGuarantors(updatedGuarantors);
    setFieldValue(`guarantor${index + 1}`, value);
  };

  const isInformalLoan = (loanTypeDetails?.loan_model ?? "").toLowerCase() === "informal";
  const effectiveMaxAmount = Number(
    borrowingEligibility?.eligible_max ?? loanTypeDetails?.max_loan_amount ?? Infinity
  );
  const typeMinAmount = Number(
    borrowingEligibility?.type_min ?? loanTypeDetails?.min_loan_amount ?? 0
  );

  const initialValues = {
    loan_type_id: "",
    duration: "",
    duration_type: "",
    amount: "",
    ...guarantors?.reduce((acc, guarantor, index) => {
      return { ...acc, [`guarantor${index + 1}`]: guarantor.value };
    }, {}),
  };
  const validationSchema = React.useMemo(
    () =>
      Yup.object({
        loan_type_id: Yup.string().required("Required"),
        duration: isInformalLoan ? Yup.number() : Yup.number().required("Required"),
        duration_type: isInformalLoan ? Yup.string() : Yup.string().required("Required"),
        amount: Yup.number()
          .transform((value, original) => {
            if (original === "" || original == null) return undefined;
            const cleaned = String(original).replace(/,/g, "").trim();
            return cleaned === "" ? undefined : Number(cleaned);
          })
          .typeError("Enter a valid amount")
          .required("Required")
          .min(typeMinAmount || 0, `Amount must be at least ₦${(typeMinAmount || 0).toLocaleString()}`)
          .max(
            Number.isFinite(effectiveMaxAmount) ? effectiveMaxAmount : Infinity,
            `Amount cannot exceed ₦${(Number.isFinite(effectiveMaxAmount) ? effectiveMaxAmount : 0).toLocaleString()}`
          ),
      }),
    [isInformalLoan, typeMinAmount, effectiveMaxAmount]
  );

  const getMemberGuarantors = (values) => {
    return Object.keys(values)
      .filter((key) => key.startsWith("guarantor") && values[key])
      .map((key) => values[key]);
  };

  const filterNonMemberGuarantors = (guarantors) => {
    return guarantors.filter(
      (item) =>
        typeof item !== "string" || !item.includes("non_member_guarantor")
    );
  };

  const [createLoanRequest, { isLoading: isCreating }] =
    useCreateMemberLoanRequestMutation();

  const handleFormSubmit = async (values, { resetForm }) => {
    if (borrowingEligibility && !borrowingEligibility.can_apply) {
      return toast.error(
        borrowingEligibility.message ||
          "Your savings and shares are below the minimum for this loan type."
      );
    }

    let { loan_type_id, duration, duration_type, amount } = values;

    // Server derives repayment_plan from duration_type for formal loans
    if (isInformalLoan) {
      duration = 0;
      duration_type = "MONTHS";
    }

    const member_guarantors = getMemberGuarantors(values);
    const formattedMemberGuarantors =
      filterNonMemberGuarantors(member_guarantors);

    // Validate guarantors before submission
    if (formattedMemberGuarantors.length > 0) {
      // Ensure all guarantor IDs are valid numbers and exist in the members list
      const validGuarantorIds = allMembers.map((m) => m.value);
      const invalidGuarantors = formattedMemberGuarantors.filter(
        (guarantorId) => !validGuarantorIds.includes(guarantorId)
      );

      if (invalidGuarantors.length > 0) {
        return toast.error(
          `Invalid guarantor(s) selected. Please select valid members from the list.`
        );
      }

      // Check for duplicate guarantors
      const uniqueGuarantors = [...new Set(formattedMemberGuarantors)];
      if (uniqueGuarantors.length !== formattedMemberGuarantors.length) {
        return toast.error(
          "Duplicate guarantors detected. Please select each member only once."
        );
      }
    }

    const payload = {
      member_id: user?.member_id,
      loan_type_id,
      duration,
      duration_type,
      amount,
      member_guarantors: formattedMemberGuarantors,
      non_member_guarantors: nonMembersGuarantorDetails,
    };

    const response = await createLoanRequest(payload);
    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occured");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occured");
    } else {
      toast.success(data?.message || "Success");
      setNonMemberGuarantorDetails([]);
      setLoanTYpeDetails({});
      setGuarantors([]);
      resetForm();
    }
  };
  const useModal = [
    {
      name: "NonMemberGuarantor",
      component: (
        <NonmemberGuarantorForm
          Back={() => setModalPage()}
          setNonMemberGuarantorDetails={setNonMemberGuarantorDetails}
        />
      ),
    },
  ];

  return (
    <>
      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={() => setModalPage()}
        centered
        size={"lg"}
      />
      <FilterFind
        back={true}
        backBtn={() => navigate(-1)}
        rightSide={false}
        title="Loan Application"
      />
      <div className="my-4 w-full flex items-center gap-4 rounded-[0.625rem]" style={{ background: "var(--color-primary-dark)" }}>
        <img
          src="/MemberSide/Icons/loan-svgrepo-com.png"
          alt="img"
          className="pl-5"
        />
        <h4 className=" flex-1 font-bold text-[2rem]  text-white ">
          Get suitable and affordable loan within <br /> 5 minutes
        </h4>
      </div>
      <CardComp cardClassName={"border-0 pt-4 pb-5"}>
        <h4 className="fw-bold ">New Application</h4>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <CardComp cardClassName={"pb-5 pt-4"}>
                <div>
                  <div>
                    <div className="row gx-0 gy-3 justify-content-between">
                      <div className="col-12 col-lg-5">
                        <SelectComp2
                          LabelClassName={"fw-2"}
                          placeholder={"Select loan type"}
                          labelText={"Loan type"}
                          arrayComp={formattedLoanTypes}
                          isLoading={isLoadingLoanTypes}
                          selectedOption={
                            values.loan_type_id != null && values.loan_type_id !== ""
                              ? (formattedLoanTypes ?? []).find(
                                  (opt) =>
                                    String(opt.value) === String(values.loan_type_id)
                                ) ?? null
                              : null
                          }
                          setSelectedOption={(selectedOption) => {
                            setLoanTYpeDetails(selectedOption ?? {});
                            setFieldValue("loan_type_id", selectedOption?.value ?? "");
                          }}
                          errors={
                            touched.loan_type_id && errors.loan_type_id
                              ? [errors.loan_type_id]
                              : []
                          }
                        />
                      </div>

                      {/* Loan Type Details */}
                      {loanTypeDetails?.id && (
                        <div className="col-12 mt-3">
                          <LoanTypeDetails loanType={loanTypeDetails} durationType={values.duration_type} />
                        </div>
                      )}

                      {!isInformalLoan && (
                        <>
                          <div className="col-12 col-lg-5">
                            <TextInputComp
                              LabelText={"Duration"}
                              borderColor="#A1A0A0"
                              LabelClassName={"fw-2"}
                              placeholder={"0"}
                              name="duration"
                              type="text"
                            />
                          </div>
                          <div className="col-12 col-lg-5">
                            <SelectComp2
                              LabelClassName={"fw-2"}
                              labelText={"Duration type"}
                              arrayComp={enums["DurationType"] ?? []}
                              selectedOption={
                                values.duration_type != null && values.duration_type !== ""
                                  ? (enums["DurationType"] ?? []).find(
                                      (opt) =>
                                        String(opt.value) === String(values.duration_type)
                                    ) ?? null
                                  : null
                              }
                              setSelectedOption={(selectedOption) =>
                                setFieldValue("duration_type", selectedOption?.value ?? "")
                              }
                              errors={
                                touched.duration_type && errors.duration_type
                                  ? [errors.duration_type]
                                  : []
                              }
                            />
                          </div>
                        </>
                      )}
                      {isInformalLoan && (
                        <div className="col-12">
                          <p className="text-muted mb-0 small">
                            Informal loans have no fixed term or schedule; you can pay any amount at any time.
                          </p>
                        </div>
                      )}
                      <div className="col-12 col-lg-5">
                        <MoneyInputComp
                          LabelText={"Amount"}
                          borderColor="#A1A0A0"
                          LabelClassName={"fw-2"}
                          placeholder={"Enter amount"}
                          descClassName="text-muted"
                          desc={
                            borrowingEligibility
                              ? `Eligible max ₦${Number(
                                  borrowingEligibility.eligible_max || 0
                                ).toLocaleString()} (savings + shares × ${
                                  borrowingEligibility.multiplier
                                })`
                              : loanTypeDetails?.min_loan_amount &&
                                loanTypeDetails?.max_loan_amount
                              ? `Amount must be between ₦${loanTypeDetails.min_loan_amount.toLocaleString()} - ₦${loanTypeDetails.max_loan_amount.toLocaleString()}`
                              : undefined
                          }
                          name={"amount"}
                        />
                        {borrowingEligibility && !borrowingEligibility.can_apply && (
                          <small className="text-danger d-block mt-1">
                            {borrowingEligibility.message}
                          </small>
                        )}
                      </div>
                      {Number(loanTypeDetails?.max_guarantor) > 0 &&
                        guarantors?.map((guarantor, index) => {
                          const storedValue = values[`guarantor${index + 1}`];
                          const maxGuarantors = loanTypeDetails?.max_guarantor || 0;
                          const selectedInOtherSlots = [];
                          for (let i = 0; i < maxGuarantors; i++) {
                            if (i !== index) {
                              const val = values[`guarantor${i + 1}`];
                              if (val && !String(val).includes("non_member_guarantor")) {
                                selectedInOtherSlots.push(String(val));
                              }
                            }
                          }
                          const availableMembers = allMembers.filter((member) => {
                            const memberValue = String(member.value);
                            const isCurrentSelection = memberValue === String(storedValue);
                            const isSelectedElsewhere = selectedInOtherSlots.includes(memberValue);
                            return isCurrentSelection || !isSelectedElsewhere;
                          });
                          const guarantorOptions = [
                            ...availableMembers,
                            {
                              value: `non_member_guarantor_${index + 1}`,
                              label: (
                                <ButtonComp
                                  btnClassName={
                                    "w-100 bg-primary1 p-2  text-white border-0 rounded"
                                  }
                                  btnText={
                                    <div
                                      className="d-flex justify-content-between align-items-center"
                                      onClick={() => {
                                        setModalPage("NonMemberGuarantor");
                                      }}
                                    >
                                      Non - member
                                      <BiChevronRight size={20} />
                                    </div>
                                  }
                                />
                              ),
                            },
                          ];
                          const selectedOptionObj =
                            storedValue != null && storedValue !== ""
                              ? guarantorOptions.find(
                                  (opt) =>
                                    String(opt.value) === String(storedValue)
                                )
                              : null;
                          return (
                          <div className="col-12 col-lg-5" key={index}>
                            <SelectComp2
                              LabelClassName={"fw-2"}
                              labelText={guarantor.label}
                              placeholder={`Select guarantor ${index + 1}`}
                              isSearchable={true}
                              isOptionDisabled={(option) => Boolean(option?.isDisabled)}
                              arrayComp={guarantorOptions}
                              selectedOption={selectedOptionObj}
                              setSelectedOption={(selectedOption) => {
                                handleGuarantorChange(
                                  index,
                                  selectedOption?.value ?? "",
                                  setFieldValue
                                );
                              }}
                              errors={
                                touched[`guarantor${index + 1}`] &&
                                errors[`guarantor${index + 1}`]
                                  ? [errors[`guarantor${index + 1}`]]
                                  : []
                              }
                            />
                          </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </CardComp>
              <div className="col-12 py-3"></div>
              <div className="d-flex justify-content-start">
                <div className="col-lg-4 col-xl-2">
                  <ButtonComp
                    type="submit"
                    btnText={"Submit application"}
                    onClick={() => {}}
                    btnClassName={
                      "w-100 bg-primary1 px-2 py-3 text-white border-0 rounded"
                    }
                    loading={isCreating}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
        {/* <Formik>
          <Form>
            <div className="row mb-5 gy-4 justify-content-between">
              <div className="col-lg-5">
                <div className="mb-4">
                  <TextInputComp labelText={"Amount"} />
                </div>
                <div className="mb-3">
                  <SelectComp2
                    labelText={"Repayment plan"}
                    placeholder="Select repayment plan"
                  />
                </div>
              </div>
              <div className="col-lg-5">
                <div className="mb-4">
                  <SelectComp2
                    labelText={"Loan purpose"}
                    placeholder="Consumption"
                  />
                </div>
                <div className="mb-4">
                  <SelectComp2
                    labelText={"Duration type"}
                    placeholder="Select repayment plan"
                  />
                </div>
                <div className="mb-3">
                  <SelectComp2
                    isMulti
                    labelText={" Guarantor"}
                    placeholder="Select guarantor"
                    arrayComp={[
                      { label: "First option", value: "First option" },
                      { label: "Second option", value: "Second option" },
                      { label: "Third option", value: "Third option" },
                      { label: "Another option", value: "Another option" },
                    ]}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-center w-100">
              <div className="col-8 col-md-6 col-lg-4">
                <ButtonComp
                  btnText={"Submit"}
                  btnClassName={"bg-primary1 w-100  text-white py-2  rounded"}
                />
              </div>
            </div>
          </Form>
        </Formik> */}
      </CardComp>
    </>
  );
}
